import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDateFormat } from "./enums/enumDateFormat.generated";
import { EnumFeatureFlag } from "./enums/enumFeatureFlag.generated";
import { EnumTransportationDistanceUnit } from "./enums/enumTransportationDistanceUnit.generated";
import { EnumVolumeUnit } from "./enums/enumVolumeUnit.generated";
import { EnumWeightUnit } from "./enums/enumWeightUnit.generated";

// Model Objects

export class CurrencyConfiguration extends ModelObject<CurrencyConfiguration> {

    /**
    * The ISO 4217 Currency Code
    * Not Nullable
    * Not Empty
    * Max Length : 6
    */
    public currencyCode: string = "";
    public currencySymbolPrefix: string = "";
    public currencySymbolSuffix: string = "";
    /**
    * The maximum number of decimal places to allow for the tenants currency
    * Not Negative
    */
    public currencyMaxDecimalPlaces: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.currencyCode = jsonSerializedObject.currencyCode;
          this.currencySymbolPrefix = jsonSerializedObject.currencySymbolPrefix;
          this.currencySymbolSuffix = jsonSerializedObject.currencySymbolSuffix;
          this.currencyMaxDecimalPlaces = jsonSerializedObject.currencyMaxDecimalPlaces;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.currencyCode = this.currencyCode;
        toRet.currencySymbolPrefix = this.currencySymbolPrefix;
        toRet.currencySymbolSuffix = this.currencySymbolSuffix;
        toRet.currencyMaxDecimalPlaces = this.currencyMaxDecimalPlaces;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "currencyCode", rule: (v: any) => v == null || v.length !== 0 || "CurrencyCode cannot be empty"},
            {field: "currencyCode", rule: (v: any) => v == null || v.length <= 6 || "CurrencyCode has too many characters (6)"},
            {field: "currencySymbolPrefix", rule: (v: any) => v == null || v.length <= 6 || "CurrencySymbolPrefix has too many characters (6)"},
            {field: "currencySymbolSuffix", rule: (v: any) => v == null || v.length <= 6 || "CurrencySymbolSuffix has too many characters (6)"},
            {field: "currencyMaxDecimalPlaces", rule: (v: any) => v == null || v >= 0 || "CurrencyMaxDecimalPlaces is not greater than minimum allowed value: 0"},
            {field: "currencyMaxDecimalPlaces", rule: (v: any) => v == null || v <= 2147483647 || "CurrencyMaxDecimalPlaces is above maximum allowed value: 2147483647"},
    ];
}


export class LicensingConfiguration extends ModelObject<LicensingConfiguration> {

    /**
    * The max number of concurrent sessions permitted
    * Not Negative
    */
    public maxConcurrentSessions: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maxConcurrentSessions = jsonSerializedObject.maxConcurrentSessions;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maxConcurrentSessions = this.maxConcurrentSessions;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maxConcurrentSessions", rule: (v: any) => v == null || v >= 0 || "MaxConcurrentSessions is not greater than minimum allowed value: 0"},
            {field: "maxConcurrentSessions", rule: (v: any) => v == null || v <= 2147483647 || "MaxConcurrentSessions is above maximum allowed value: 2147483647"},
    ];
}


export class DateAndTimeConfiguration extends ModelObject<DateAndTimeConfiguration> {

    /**
    * The format to show dates in the system. See https://nodatime.org/2.4.x/userguide/zoneddatetime-patterns
    */
    public dateFormat: EnumDateFormat = EnumDateFormat.yYYYMMDD;
    /**
    * The format to show times in the system. See https://nodatime.org/2.4.x/userguide/zoneddatetime-patterns
    * Not Nullable
    * Not Empty
    */
    public timeFormat: string = "";
    /**
    * IANA Timezone 2019a
    * Not Nullable
    * Not Empty
    */
    public timezoneName: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.dateFormat = EnumDateFormat.getByValue(jsonSerializedObject.dateFormat, this.dateFormat);
          this.timeFormat = jsonSerializedObject.timeFormat;
          this.timezoneName = jsonSerializedObject.timezoneName;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.dateFormat = this.dateFormat.value;
        toRet.timeFormat = this.timeFormat;
        toRet.timezoneName = this.timezoneName;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "timeFormat", rule: (v: any) => v == null || v.length !== 0 || "TimeFormat cannot be empty"},
            {field: "timezoneName", rule: (v: any) => v == null || v.length !== 0 || "TimezoneName cannot be empty"},
    ];
}


export class NumberConfiguration extends ModelObject<NumberConfiguration> {

    public thousandsSeparator: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.thousandsSeparator = jsonSerializedObject.thousandsSeparator;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.thousandsSeparator = this.thousandsSeparator;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "thousandsSeparator", rule: (v: any) => v == null || v.length <= 1 || "ThousandsSeparator has too many characters (1)"},
    ];
}


export class LocationConfigurationCalculated extends ModelObject<LocationConfigurationCalculated> {

    /**
    * Two character top-level-domain code for the country
    * Not Nullable
    * Preserves leading and trailing whitespce.
    */
    public countryCodeTopLevelDomain: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.countryCodeTopLevelDomain = jsonSerializedObject.countryCodeTopLevelDomain;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.countryCodeTopLevelDomain = this.countryCodeTopLevelDomain;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class LocationConfiguration extends ModelObject<LocationConfiguration> {

    /**
    * ISO 3166-1 alpha-2 country code
    * Not Nullable
    * Not Empty
    */
    public locationCountryCode: string = "";
    public calculated: LocationConfigurationCalculated = new LocationConfigurationCalculated();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.locationCountryCode = jsonSerializedObject.locationCountryCode;
          this.calculated = new LocationConfigurationCalculated(jsonSerializedObject.calculated);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.locationCountryCode = this.locationCountryCode;
        toRet.calculated = this.calculated.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "locationCountryCode", rule: (v: any) => v == null || v.length !== 0 || "LocationCountryCode cannot be empty"},
            {field: "calculated", rule: (v: any) => !!v || "calculated is required"},
            {field: "calculated", rule: (v: any) => v.validate()},
    ];
}


export class LanguageAndCultureConfiguration extends ModelObject<LanguageAndCultureConfiguration> {

    /**
    * Combination of an ISO 639 two-letter lowercase culture code associated with a language and an ISO 3166 two-letter uppercase subculture code associated with a country or region.
    * Not Nullable
    * Not Empty
    * Max Length : 5
    */
    public cultureCode: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.cultureCode = jsonSerializedObject.cultureCode;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.cultureCode = this.cultureCode;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "cultureCode", rule: (v: any) => v == null || v.length !== 0 || "CultureCode cannot be empty"},
            {field: "cultureCode", rule: (v: any) => v == null || v.length <= 5 || "CultureCode has too many characters (5)"},
    ];
}


export class DisplayUnitPreferencesReadOnly extends ModelObject<DisplayUnitPreferencesReadOnly> {

    public generalVolumesConversionMultiplierFromLitre: number = 0;
    public vesselVolumesConversionMultiplierFromLitre: number = 0;
    public generalWeightsConversionMultiplierFromKilogram: number = 0;
    public transportationDistancesConversionMultiplierFromKilometre: number = 0;
    public generalVolumesDisplayDps: number = 0;
    public vesselVolumesDisplayDps: number = 0;
    public generalWeightsDisplayDps: number = 0;
    public transportationDistanceDisplayDps: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.generalVolumesConversionMultiplierFromLitre = jsonSerializedObject.generalVolumesConversionMultiplierFromLitre;
          this.vesselVolumesConversionMultiplierFromLitre = jsonSerializedObject.vesselVolumesConversionMultiplierFromLitre;
          this.generalWeightsConversionMultiplierFromKilogram = jsonSerializedObject.generalWeightsConversionMultiplierFromKilogram;
          this.transportationDistancesConversionMultiplierFromKilometre = jsonSerializedObject.transportationDistancesConversionMultiplierFromKilometre;
          this.generalVolumesDisplayDps = jsonSerializedObject.generalVolumesDisplayDps;
          this.vesselVolumesDisplayDps = jsonSerializedObject.vesselVolumesDisplayDps;
          this.generalWeightsDisplayDps = jsonSerializedObject.generalWeightsDisplayDps;
          this.transportationDistanceDisplayDps = jsonSerializedObject.transportationDistanceDisplayDps;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.generalVolumesConversionMultiplierFromLitre = this.generalVolumesConversionMultiplierFromLitre;
        toRet.vesselVolumesConversionMultiplierFromLitre = this.vesselVolumesConversionMultiplierFromLitre;
        toRet.generalWeightsConversionMultiplierFromKilogram = this.generalWeightsConversionMultiplierFromKilogram;
        toRet.transportationDistancesConversionMultiplierFromKilometre = this.transportationDistancesConversionMultiplierFromKilometre;
        toRet.generalVolumesDisplayDps = this.generalVolumesDisplayDps;
        toRet.vesselVolumesDisplayDps = this.vesselVolumesDisplayDps;
        toRet.generalWeightsDisplayDps = this.generalWeightsDisplayDps;
        toRet.transportationDistanceDisplayDps = this.transportationDistanceDisplayDps;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "generalVolumesDisplayDps", rule: (v: any) => v == null || v >= -2147483648 || "GeneralVolumesDisplayDps is not greater than minimum allowed value: -2147483648"},
            {field: "generalVolumesDisplayDps", rule: (v: any) => v == null || v <= 2147483647 || "GeneralVolumesDisplayDps is above maximum allowed value: 2147483647"},
            {field: "vesselVolumesDisplayDps", rule: (v: any) => v == null || v >= -2147483648 || "VesselVolumesDisplayDps is not greater than minimum allowed value: -2147483648"},
            {field: "vesselVolumesDisplayDps", rule: (v: any) => v == null || v <= 2147483647 || "VesselVolumesDisplayDps is above maximum allowed value: 2147483647"},
            {field: "generalWeightsDisplayDps", rule: (v: any) => v == null || v >= -2147483648 || "GeneralWeightsDisplayDps is not greater than minimum allowed value: -2147483648"},
            {field: "generalWeightsDisplayDps", rule: (v: any) => v == null || v <= 2147483647 || "GeneralWeightsDisplayDps is above maximum allowed value: 2147483647"},
            {field: "transportationDistanceDisplayDps", rule: (v: any) => v == null || v >= -2147483648 || "TransportationDistanceDisplayDps is not greater than minimum allowed value: -2147483648"},
            {field: "transportationDistanceDisplayDps", rule: (v: any) => v == null || v <= 2147483647 || "TransportationDistanceDisplayDps is above maximum allowed value: 2147483647"},
    ];
}


export class DisplayUnitPreferences extends ModelObject<DisplayUnitPreferences> {

    public generalVolumes: EnumVolumeUnit = EnumVolumeUnit.none;
    public vesselVolumes: EnumVolumeUnit = EnumVolumeUnit.none;
    public generalWeights: EnumWeightUnit = EnumWeightUnit.none;
    public transportationDistances: EnumTransportationDistanceUnit = EnumTransportationDistanceUnit.none;
    public readOnly: DisplayUnitPreferencesReadOnly = new DisplayUnitPreferencesReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.generalVolumes = EnumVolumeUnit.getByValue(jsonSerializedObject.generalVolumes, this.generalVolumes);
          this.vesselVolumes = EnumVolumeUnit.getByValue(jsonSerializedObject.vesselVolumes, this.vesselVolumes);
          this.generalWeights = EnumWeightUnit.getByValue(jsonSerializedObject.generalWeights, this.generalWeights);
          this.transportationDistances = EnumTransportationDistanceUnit.getByValue(jsonSerializedObject.transportationDistances, this.transportationDistances);
          this.readOnly = new DisplayUnitPreferencesReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.generalVolumes = this.generalVolumes.value;
        toRet.vesselVolumes = this.vesselVolumes.value;
        toRet.generalWeights = this.generalWeights.value;
        toRet.transportationDistances = this.transportationDistances.value;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class TenantReadOnly extends ModelObject<TenantReadOnly> {

    /**
    * Timestamp at which the tenant was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * The maximum number of emails this tenant can send per day if the tenant has active billing status.
    * Not Negative
    */
    public dailyEmailQuota: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.dailyEmailQuota = jsonSerializedObject.dailyEmailQuota;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        toRet.dailyEmailQuota = this.dailyEmailQuota;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "dailyEmailQuota", rule: (v: any) => v == null || v >= 0 || "DailyEmailQuota is not greater than minimum allowed value: 0"},
            {field: "dailyEmailQuota", rule: (v: any) => v == null || v <= 2147483647 || "DailyEmailQuota is above maximum allowed value: 2147483647"},
    ];
}


export class Tenant extends ModelObject<Tenant> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * The name of the tenant
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * The name of the data set
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public dataSetName: string = "";
    /**
    * Tenant group id
    */
    public tenantGroupId: Guid = Guid.createEmpty();
    /**
    * When true, indicates the tenant is the primary tenant for this tenant group.
    */
    public isPrimary: boolean = false;
    /**
    * When true, indicates the tenant is a demo data set.
    */
    public isDemoData: boolean = false;
    /**
    * The set of flag protected features this tenant has access to.
    */
    public featureFlags: EnumFeatureFlag[] = [];
    public currencyConfiguration: CurrencyConfiguration = new CurrencyConfiguration();
    public licensingConfiguration: LicensingConfiguration = new LicensingConfiguration();
    public dateAndTimeConfiguration: DateAndTimeConfiguration = new DateAndTimeConfiguration();
    public locationConfiguration: LocationConfiguration = new LocationConfiguration();
    public displayUnitPreferences: DisplayUnitPreferences = new DisplayUnitPreferences();
    public languageAndCultureConfiguration: LanguageAndCultureConfiguration = new LanguageAndCultureConfiguration();
    public readOnly: TenantReadOnly = new TenantReadOnly();
    public numberConfiguration: NumberConfiguration = new NumberConfiguration();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.dataSetName = jsonSerializedObject.dataSetName;
          this.tenantGroupId = Guid.parseOrEmpty(jsonSerializedObject.tenantGroupId);
          this.isPrimary = jsonSerializedObject.isPrimary;
          this.isDemoData = jsonSerializedObject.isDemoData;
          this.featureFlags = jsonSerializedObject.featureFlags.map((v: any) => EnumFeatureFlag.getByValue(v));
          this.currencyConfiguration = new CurrencyConfiguration(jsonSerializedObject.currencyConfiguration);
          this.licensingConfiguration = new LicensingConfiguration(jsonSerializedObject.licensingConfiguration);
          this.dateAndTimeConfiguration = new DateAndTimeConfiguration(jsonSerializedObject.dateAndTimeConfiguration);
          this.locationConfiguration = new LocationConfiguration(jsonSerializedObject.locationConfiguration);
          this.displayUnitPreferences = new DisplayUnitPreferences(jsonSerializedObject.displayUnitPreferences);
          this.languageAndCultureConfiguration = new LanguageAndCultureConfiguration(jsonSerializedObject.languageAndCultureConfiguration);
          this.readOnly = new TenantReadOnly(jsonSerializedObject.readOnly);
          this.numberConfiguration = new NumberConfiguration(jsonSerializedObject.numberConfiguration);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.dataSetName = this.dataSetName;
        toRet.tenantGroupId = this.tenantGroupId.toString();
        toRet.isPrimary = this.isPrimary;
        toRet.isDemoData = this.isDemoData;
        toRet.featureFlags = this.featureFlags.map(v => v.value);
        toRet.currencyConfiguration = this.currencyConfiguration.toJsonSerializedObject();
        toRet.licensingConfiguration = this.licensingConfiguration.toJsonSerializedObject();
        toRet.dateAndTimeConfiguration = this.dateAndTimeConfiguration.toJsonSerializedObject();
        toRet.locationConfiguration = this.locationConfiguration.toJsonSerializedObject();
        toRet.displayUnitPreferences = this.displayUnitPreferences.toJsonSerializedObject();
        toRet.languageAndCultureConfiguration = this.languageAndCultureConfiguration.toJsonSerializedObject();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.numberConfiguration = this.numberConfiguration.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "Tenant.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "dataSetName", rule: (v: any) => v == null || v.length !== 0 || "DataSetName cannot be empty"},
            {field: "dataSetName", rule: (v: any) => v == null || v.length <= 1000 || "DataSetName has too many characters (1000)"},
            {field: "tenantGroupId", rule: (v: any) => !v.isEmpty() || "Tenant.TenantGroupId cannot be empty"},
            {field: "currencyConfiguration", rule: (v: any) => !!v || "currencyConfiguration is required"},
            {field: "currencyConfiguration", rule: (v: any) => v.validate()},
            {field: "licensingConfiguration", rule: (v: any) => !!v || "licensingConfiguration is required"},
            {field: "licensingConfiguration", rule: (v: any) => v.validate()},
            {field: "dateAndTimeConfiguration", rule: (v: any) => !!v || "dateAndTimeConfiguration is required"},
            {field: "dateAndTimeConfiguration", rule: (v: any) => v.validate()},
            {field: "locationConfiguration", rule: (v: any) => !!v || "locationConfiguration is required"},
            {field: "locationConfiguration", rule: (v: any) => v.validate()},
            {field: "displayUnitPreferences", rule: (v: any) => !!v || "displayUnitPreferences is required"},
            {field: "displayUnitPreferences", rule: (v: any) => v.validate()},
            {field: "languageAndCultureConfiguration", rule: (v: any) => !!v || "languageAndCultureConfiguration is required"},
            {field: "languageAndCultureConfiguration", rule: (v: any) => v.validate()},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "numberConfiguration", rule: (v: any) => !!v || "numberConfiguration is required"},
            {field: "numberConfiguration", rule: (v: any) => v.validate()},
    ];
}


export class TenantGroupReadOnly extends ModelObject<TenantGroupReadOnly> {

    /**
    * Timestamp at which the tenant group was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class TenantGroup extends ModelObject<TenantGroup> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * The name of the tenant group
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public readOnly: TenantGroupReadOnly = new TenantGroupReadOnly();
    /**
    * Referral code assigned when signing up.
    * Not Nullable
    * Preserves leading and trailing whitespce.
    */
    public referralCode: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.readOnly = new TenantGroupReadOnly(jsonSerializedObject.readOnly);
          this.referralCode = jsonSerializedObject.referralCode;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.referralCode = this.referralCode;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "TenantGroup.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class TenantAndTenantGroupId extends ModelObject<TenantAndTenantGroupId> {

    public tenantId: Guid = Guid.createEmpty();
    public tenantGroupId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.tenantGroupId = Guid.parseOrEmpty(jsonSerializedObject.tenantGroupId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantId = this.tenantId.toString();
        toRet.tenantGroupId = this.tenantGroupId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "TenantAndTenantGroupId.TenantId cannot be empty"},
            {field: "tenantGroupId", rule: (v: any) => !v.isEmpty() || "TenantAndTenantGroupId.TenantGroupId cannot be empty"},
    ];
}


export class TenantService extends ServiceBase {

  public async getTenant(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Tenant> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => new Tenant(response.tenant);;
    return this.makeJsonRequest("tenant/v1/getTenant", req, false, extractor, opts);
  };

  public async getTenantsForTenantGroup(tenantGroupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Tenant[]> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    };
    const extractor = (response:any) => response.tenants.map((v: any) => new Tenant(v));;
    return this.makeJsonRequest("tenant/v1/getTenantsForTenantGroup", req, false, extractor, opts);
  };

  public async updateCurrencyConfiguration(tenantId: Guid, currencyConfiguration: CurrencyConfiguration, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    currencyConfiguration:currencyConfiguration.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("tenant/v1/updateCurrencyConfiguration", req, true, extractor, opts);
  };

  public async getTenantGroup(tenantGroupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<TenantGroup> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    };
    const extractor = (response:any) => new TenantGroup(response.tenantGroup);;
    return this.makeJsonRequest("tenant/v1/getTenantGroup", req, false, extractor, opts);
  };

  public async updateDateAndTimeConfiguration(tenantId: Guid, dateAndTimeConfiguration: DateAndTimeConfiguration, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    dateAndTimeConfiguration:dateAndTimeConfiguration.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("tenant/v1/updateDateAndTimeConfiguration", req, true, extractor, opts);
  };

  public async updateNumberConfiguration(tenantId: Guid, numberConfiguration: NumberConfiguration, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    numberConfiguration:numberConfiguration.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("tenant/v1/updateNumberConfiguration", req, true, extractor, opts);
  };

  public async updateLanguageAndCultureConfiguration(tenantId: Guid, languageAndCultureConfiguration: LanguageAndCultureConfiguration, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    languageAndCultureConfiguration:languageAndCultureConfiguration.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("tenant/v1/updateLanguageAndCultureConfiguration", req, true, extractor, opts);
  };

  public async findAvailableMultiTenantTenants(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Tenant[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.tenants.map((v: any) => new Tenant(v));;
    return this.makeJsonRequest("tenant/v1/findAvailableMultiTenantTenants", req, false, extractor, opts);
  };
}

export const tenantService = new TenantService();